import React, { useState, useRef, useEffect } from "react";
import { useFinance } from "../context/FinanceContext";
import "./ActionPanel.css";

function fmt(n) {
  return "₹" + Number(n).toLocaleString("en-IN");
}

function getNow() {
  const d = new Date();
  const date = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  return { date, time };
}

const OFFICES = ["Amara Infinites", "Melvine Solution", "Personal", "Ragnaor"];

// ── Income Service Catalog ────────────────────────────────────────────────
const SERVICE_CATALOG = {
  "Business Registration": [
    "Sole Proprietorship (MSME+GST)", "Partnership firm", "Limited Liability Partnership",
    "Private Limited Company", "One Person Company", "Startup India Registration", "StartupTN",
  ],
  "GST & Tax": ["GST registration", "GST Cancellation/closure", "GST Revocation", "GST filing", "IT filing"],
  "Supportives": ["MSME", "Individual DSC", "Organization DSC", "Business PAN", "AD Code Registration", "Trademark Registration"],
  "License": ["FSSAI basic", "FSSAI state", "FSSAI central", "Import Export Code", "Shop and Establishment license"],
  "MCA Services": ["Private Limited Annual ROC Compliance", "LLP Annual ROC Compliance", "OPC Annual ROC Compliance",
    "Director change (Add/remove)", "Authorized Capital Increase", "Winding up (excludes form charge)", "MOA, AOA Amendment", "Registered office change"],
  "Drafting": ["Sale agreement", "Employment agreement", "Rental agreement", "Agreement drafting"],
  "Design": ["Logo designing", "Package designing", "Visiting card designing", "Designer Visiting card", "Designer Letterhead"],
  "Websites & E Commerce": ["Web designing", "Standard Website (no payment)", "Standard eCommerce Website", "Customized Website", "Amazon account set up"],
};

const ALL_SERVICES = Object.entries(SERVICE_CATALOG).flatMap(([category, services]) =>
  services.map((name) => ({ category, name, id: `${category}:${name}` }))
);

// ── Expense Reason Catalog ─────────────────────────────────────────────────

const EXPENSE_CATALOG = {
  Bill:        { icon: "📄", subs: {
    "EB":              ["N/A"],
    "Mobile Recharge": ["Airtel", "Jio", "BSNL", "Vi"],
    "IB Bill":         ["Airtel", "Jio", "ACT"],
  }},
  Pantry:      { icon: "🍵", subs: {
    "Tea":     ["N/A"],
    "Water":   ["Can", "Bottle"],
    "Biscuit": ["N/A"],
    "Snacks":  ["N/A"],
  }},
  Stationary:  { icon: "✏️", subs: {
    "A4 Sheet": ["N/A"],
    "Pen":      ["N/A"],
    "Pencil":   ["N/A"],
  }},
  Courier:     { icon: "📦", subs: {
    "Courier": ["N/A"],
  }},
  MCA:         { icon: "🏢", subs: {
    "PL Name Approval":  ["N/A"],
    "LLP Name Approval": ["N/A"],
    "PAN":               ["N/A"],
    "Stamp (PL)":        ["N/A"],
    "Incorp(LLP)":       ["N/A"],
  }},
  FSSAI:       { icon: "🍃", subs: {
    "Basic Reg":      ["N/A"],
    "State License":  ["N/A"],
    "Central License":["N/A"],
  }},
  GST:         { icon: "🧾", subs: {
    "Filling":           ["N/A"],
    "Late Fee":          ["N/A"],
    "Filling & Late Fee":["N/A"],
    "Token":             ["N/A"],
  }},
  TNRegistry:  { icon: "🏛️", subs: {
    "Firm":  ["N/A"],
    "Trust": ["N/A"],
  }},
  PAN:         { icon: "🪪", subs: {
    "With HardCopy": ["N/A"],
    "Only Softcopy": ["N/A"],
  }},
  BankCharges: { icon: "🏦", subs: {
    "IMPS": ["N/A"],
    "NEFT": ["N/A"],
  }},
  DSC:         { icon: "💻", subs: {
    "Individual":   ["N/A"],
    "Organization": ["N/A"],
  }},
  Pet:         { icon: "🐾", subs: {
    "Milk":      ["N/A"],
    "Food":      ["Dry Food", "Wet Food"],
    "Chicken":   ["N/A"],
    "Curd":      ["N/A"],
    "Injection": ["Vaccine", "Treatment"],
  }},
  Rent:        { icon: "🏠", subs: {
    "Home":   ["N/A"],
    "Office": ["N/A"],
  }},
  Market:      { icon: "🛒", subs: {
    "Groceries":  ["N/A"],
    "Rice":       ["N/A"],
    "Vegetables": ["N/A"],
  }},
  Fuel:        { icon: "⛽", subs: {
    "Petrol": ["N/A"],
    "Diesel": ["N/A"],
  }},
};

const OTHER_ID     = "Other:__other__";
const EXP_OTHER_ID = "ExpOther:__other__";

// ── Income Service Modal ──────────────────────────────────────────────────
function ServiceModal({ onDone, onClose }) {
  const [selectedCategory, setSelectedCategory] = useState("Business Registration");
  const [searchQuery, setSearchQuery]           = useState("");
  const [selected, setSelected]                 = useState(null);
  const [otherText, setOtherText]               = useState("");
  const otherInputRef = useRef();

  useEffect(() => {
    if (selected === OTHER_ID) setTimeout(() => otherInputRef.current?.focus(), 50);
  }, [selected]);

  const filtered = searchQuery.trim()
    ? ALL_SERVICES.filter(
        (s) => s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
               s.category.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : ALL_SERVICES.filter((s) => s.category === selectedCategory);

  const hasNoResults     = searchQuery.trim() && filtered.length === 0;
  const isOtherCatActive = !searchQuery && selectedCategory === "Other";
  const canConfirm       = selected && (selected !== OTHER_ID || otherText.trim().length > 0);

  const toggle = (id) => { setSelected((prev) => (prev === id ? null : id)); if (id !== OTHER_ID) setOtherText(""); };
  const handleConfirm = () => {
    if (selected === OTHER_ID) onDone(otherText.trim());
    else onDone(ALL_SERVICES.find((s) => s.id === selected)?.name || "");
  };

  const previewLabel = selected === OTHER_ID
    ? (otherText.trim() ? `✓ ${otherText.trim()}` : "✓ Other — specify below")
    : selected ? `✓ ${ALL_SERVICES.find((s) => s.id === selected)?.name}` : null;

  return (
    <div className="sm-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sm-modal">
        <div className="sm-header">
          <span className="sm-title">Select Service</span>
          <button className="sm-close" onClick={onClose}>×</button>
          <div className="sm-search-wrap">
            <span className="sm-search-icon">🔍</span>
            <input className="sm-search" type="text" placeholder="Search services..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} autoFocus />
            {searchQuery && <button className="sm-search-clear" onClick={() => setSearchQuery("")}>×</button>}
          </div>
        </div>
        <div className="sm-body">
          {!searchQuery && (
            <div className="sm-categories">
              {Object.keys(SERVICE_CATALOG).map((cat) => (
                <button key={cat} className={`sm-cat-btn ${selectedCategory === cat ? "sm-cat-btn--active" : ""}`}
                  onClick={() => setSelectedCategory(cat)}>{cat}</button>
              ))}
              <div className="sm-cat-divider" />
              <button className={`sm-cat-btn sm-cat-btn--other ${selectedCategory === "Other" ? "sm-cat-btn--active sm-cat-btn--other-active" : ""}`}
                onClick={() => setSelectedCategory("Other")}>Other</button>
            </div>
          )}
          <div className="sm-services">
            {searchQuery && (
              <div className="sm-search-info">
                {filtered.length > 0 ? `${filtered.length} result${filtered.length > 1 ? "s" : ""} for "${searchQuery}"` : `No results for "${searchQuery}"`}
              </div>
            )}
            {!searchQuery && <div className="sm-category-heading">{selectedCategory}</div>}
            {isOtherCatActive ? (
              <div className={`sm-service-item sm-service-item--other ${selected === OTHER_ID ? "sm-service-item--selected" : ""}`} onClick={() => toggle(OTHER_ID)}>
                <span className={`sm-radio ${selected === OTHER_ID ? "sm-radio--on" : ""}`} />
                <div className="sm-other-content">
                  <span className="sm-service-name">Other Service</span>
                  <span className="sm-other-hint">Specify a custom service not listed above</span>
                </div>
              </div>
            ) : hasNoResults ? (
              <div className="sm-no-results">No services found</div>
            ) : (
              filtered.map((s) => (
                <div key={s.id} className={`sm-service-item ${selected === s.id ? "sm-service-item--selected" : ""}`} onClick={() => toggle(s.id)}>
                  <span className={`sm-radio ${selected === s.id ? "sm-radio--on" : ""}`} />
                  {searchQuery && <span className="sm-service-cat-badge">{s.category}</span>}
                  <span className="sm-service-name">{s.name}</span>
                </div>
              ))
            )}
            {selected === OTHER_ID && (
              <div className="sm-other-input-wrap">
                <label className="sm-other-input-label">Describe the service</label>
                <input ref={otherInputRef} className="sm-other-input" type="text"
                  placeholder="e.g. Annual filing, Custom consulting..."
                  value={otherText} onChange={(e) => setOtherText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && canConfirm && handleConfirm()} />
                {otherText.trim() && <span className="sm-other-input-check">✓ Ready to confirm</span>}
              </div>
            )}
          </div>
        </div>
        <div className="sm-footer">
          {previewLabel ? <span className="sm-selected-preview">{previewLabel}</span> : <span className="sm-none-hint">No service selected</span>}
          <div className="sm-footer-actions">
            <button className="sm-btn-cancel" onClick={onClose}>Cancel</button>
            <button className="sm-btn-done" disabled={!canConfirm} onClick={handleConfirm}>Confirm</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Expense Reason Modal ──────────────────────────────────────────────────
function ExpenseReasonModal({ onDone, onClose }) {
  const [catalog, setCatalog] = useState(() => {
    try {
      const saved = localStorage.getItem("expenseCatalog");
      return saved ? JSON.parse(saved) : EXPENSE_CATALOG;
    } catch { return EXPENSE_CATALOG; }
  });

  const expCategories = Object.keys(catalog);
  const [selectedCat,    setSelectedCat]    = useState(expCategories[0]);
  const [selectedSub,    setSelectedSub]    = useState(null);
  const [selectedSubSub, setSelectedSubSub] = useState(null);
  const [otherText,      setOtherText]      = useState("");
  const [searchQuery,    setSearchQuery]    = useState("");
  const [addingSubTo,  setAddingSubTo]  = useState(null);
  const [newSubText,   setNewSubText]   = useState("");
  const [addingCat,    setAddingCat]    = useState(false);
  const [newCatText,   setNewCatText]   = useState("");
  const newSubRef     = useRef();
  const newCatRef     = useRef();
  const otherInputRef = useRef();

  useEffect(() => {
    if (selectedSub === EXP_OTHER_ID) setTimeout(() => otherInputRef.current?.focus(), 50);
  }, [selectedSub]);
  useEffect(() => {
    if (addingSubTo) setTimeout(() => newSubRef.current?.focus(), 50);
  }, [addingSubTo]);

  useEffect(() => {
    if (addingCat) setTimeout(() => newCatRef.current?.focus(), 50);
  }, [addingCat]);

  const saveCatalog = (updated) => {
    setCatalog(updated);
    try { localStorage.setItem("expenseCatalog", JSON.stringify(updated)); } catch {}
  };

  const handleAddSub = (cat) => {
    const name = newSubText.trim();
    if (!name) return;
    const updated = {
      ...catalog,
      [cat]: { ...catalog[cat], subs: { ...catalog[cat].subs, [name]: ["N/A"] } },
    };
    saveCatalog(updated);
    setNewSubText("");
    setAddingSubTo(null);
    setSelectedSub(`${cat}:${name}`);
    setSelectedSubSub(null);
  };

  const handleAddCat = () => {
    const name = newCatText.trim();
    if (!name || catalog[name]) return;
    const updated = { ...catalog, [name]: { icon: "📁", subs: {} } };
    saveCatalog(updated);
    setNewCatText("");
    setAddingCat(false);
    setSelectedCat(name);
    setSelectedSub(null);
  };

  const allSubs = Object.entries(catalog).flatMap(([cat, { subs }]) =>
    Object.keys(subs).map((subName) => ({ cat, sub: subName, id: `${cat}:${subName}` }))
  );
  const filtered = searchQuery.trim()
    ? allSubs.filter(
        (s) => s.sub.toLowerCase().includes(searchQuery.toLowerCase()) ||
               s.cat.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : null;

  const isOtherCat = !searchQuery && selectedCat === "Other";
  const isOtherSub = selectedSub === EXP_OTHER_ID;

  const currentSubSubs = selectedSub && !isOtherSub
    ? (() => { const [cat, subName] = selectedSub.split(":"); return catalog[cat]?.subs[subName] || []; })()
    : [];
  const onlyNA = currentSubSubs.length === 1 && currentSubSubs[0] === "N/A";

  const canConfirm = isOtherSub
  ? otherText.trim().length > 0
  : !!selectedSub;

  const handleConfirm = () => {
  if (isOtherSub) { onDone(otherText.trim()); return; }
  const [cat, subName] = selectedSub.split(":");
  onDone(`${cat} — ${subName}`);
};

  const previewLabel = isOtherSub
  ? (otherText.trim() ? `✓ ${otherText.trim()}` : "✓ Other — specify below")
  : selectedSub
    ? (() => { const [cat, sub] = selectedSub.split(":"); return `✓ ${cat} — ${sub}`; })()
    : null;

  return (
    <div className="sm-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sm-modal sm-modal--wide">

        {/* Header */}
        <div className="sm-header">
          <span className="sm-title">Select Reason</span>
          <button className="sm-close" onClick={onClose}>×</button>
          <div className="sm-search-wrap">
            <span className="sm-search-icon">🔍</span>
            <input className="sm-search sm-search--red" type="text" placeholder="Search reason..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setSelectedSub(null); setSelectedSubSub(null); }}
              autoFocus />
            {searchQuery && <button className="sm-search-clear" onClick={() => setSearchQuery("")}>×</button>}
          </div>
        </div>

        <div className="sm-body">

          {/* Column 1: Categories */}
          {!searchQuery && (
            <div className="sm-categories">
              {expCategories.map((cat) => (
  <button key={cat}
    className={`sm-cat-btn sm-cat-btn--red-hover ${selectedCat === cat ? "sm-cat-btn--active-red" : ""}`}
    onClick={() => { setSelectedCat(cat); setSelectedSub(null); setSelectedSubSub(null); setAddingSubTo(null); }}>
    {catalog[cat].icon} {cat}
  </button>
))}
<div className="sm-cat-divider" />
{addingCat ? (
  <div className="sm-add-inline" style={{ margin: "4px 8px" }}>
    <input ref={newCatRef} className="sm-add-input" type="text"
      placeholder="Category name"
      value={newCatText} onChange={(e) => setNewCatText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") handleAddCat();
        if (e.key === "Escape") { setAddingCat(false); setNewCatText(""); }
      }} />
    <div className="sm-add-inline-actions">
      <button className="sm-add-confirm-btn" disabled={!newCatText.trim()} onClick={handleAddCat}>Add</button>
      <button className="sm-add-cancel-btn" onClick={() => { setAddingCat(false); setNewCatText(""); }}>✕</button>
    </div>
  </div>
) : (
  <button className="sm-cat-btn" style={{ color: "#ef4444", fontWeight: 700 }}
    onClick={() => { setAddingCat(true); setNewCatText(""); }}>
    + Add Category
  </button>
)}
<div className="sm-cat-divider" />
<button
  className={`sm-cat-btn sm-cat-btn--other ${selectedCat === "Other" ? "sm-cat-btn--active sm-cat-btn--other-active" : ""}`}
  onClick={() => { setSelectedCat("Other"); setSelectedSub(EXP_OTHER_ID); setSelectedSubSub(null); }}>
  Other
</button>
            </div>
          )}

          {/* Column 2: Subs */}
{!searchQuery && !isOtherCat && (
  <div className="sm-subcategories">
    <div className="sm-subcat-header">
  <span className="sm-category-heading" style={{ padding: 0 }}>
    {catalog[selectedCat]?.icon} {selectedCat}
  </span>
  {addingSubTo !== selectedCat && (
    <button className="sm-add-icon-btn" title="Add sub-type"
      onClick={() => { setAddingSubTo(selectedCat); setNewSubText(""); }}>+</button>
  )}
</div>

              {Object.keys(catalog[selectedCat]?.subs || {}).map((subName) => {
                const subId = `${selectedCat}:${subName}`;
                return (
                  <div key={subId}
                    className={`sm-service-item sm-service-item--red-row ${selectedSub === subId ? "sm-service-item--selected-red" : ""}`}
                    onClick={() => { setSelectedSub(subId); setSelectedSubSub(null); }}>
                    <span className={`sm-radio ${selectedSub === subId ? "sm-radio--on sm-radio--on-red" : ""}`} />
                    <span className="sm-service-name">{subName}</span>
                  </div>
                );
              })}

              {addingSubTo === selectedCat && (
                <div className="sm-add-inline">
                  <input ref={newSubRef} className="sm-add-input" type="text"
                    placeholder="e.g. Enter here"
                    value={newSubText} onChange={(e) => setNewSubText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAddSub(selectedCat);
                      if (e.key === "Escape") { setAddingSubTo(null); setNewSubText(""); }
                    }} />
                  <div className="sm-add-inline-actions">
                    <button className="sm-add-confirm-btn" disabled={!newSubText.trim()} onClick={() => handleAddSub(selectedCat)}>Add</button>
                    <button className="sm-add-cancel-btn" onClick={() => { setAddingSubTo(null); setNewSubText(""); }}>✕</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Column 3: Leaves / Other / Search */}
          <div className="sm-services" style={{ display: searchQuery || isOtherCat || selectedSub === EXP_OTHER_ID ? "flex" : "none" }}>            {searchQuery && (
              <>
                <div className="sm-search-info">
                  {filtered.length > 0
                    ? `${filtered.length} result${filtered.length > 1 ? "s" : ""} for "${searchQuery}"`
                    : `No results for "${searchQuery}"`}
                </div>
                {filtered.length === 0
                  ? <div className="sm-no-results">No results found</div>
                  : filtered.map((item) => (
                      <div key={item.id}
                        className={`sm-service-item sm-service-item--red-row ${selectedSub === item.id ? "sm-service-item--selected-red" : ""}`}
                        onClick={() => { setSelectedSub(item.id); setSelectedSubSub(null); setSearchQuery(""); setSelectedCat(item.cat); }}>
                        <span className={`sm-radio ${selectedSub === item.id ? "sm-radio--on sm-radio--on-red" : ""}`} />
                        <span className="sm-service-cat-badge">{item.cat}</span>
                        <span className="sm-service-name">{item.sub}</span>
                      </div>
                    ))
                }
              </>
            )}

            {!searchQuery && isOtherCat && (
              <div className={`sm-service-item sm-service-item--other ${selectedSub === EXP_OTHER_ID ? "sm-service-item--selected" : ""}`}
                onClick={() => setSelectedSub(EXP_OTHER_ID)}>
                <span className={`sm-radio ${selectedSub === EXP_OTHER_ID ? "sm-radio--on" : ""}`} />
                <div className="sm-other-content">
                  <span className="sm-service-name">Other Reason</span>
                  <span className="sm-other-hint">Type a custom reason not listed above</span>
                </div>
              </div>
            )}


            {selectedSub === EXP_OTHER_ID && (
              <div className="sm-other-input-wrap">
                <label className="sm-other-input-label">Describe the reason</label>
                <input ref={otherInputRef} className="sm-other-input" type="text"
                  placeholder="e.g. Repair, Transport, Misc..."
                  value={otherText} onChange={(e) => setOtherText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && canConfirm && handleConfirm()} />
                {otherText.trim() && <span className="sm-other-input-check">✓ Ready to confirm</span>}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sm-footer sm-footer--red">
          {previewLabel
            ? <span className="sm-selected-preview sm-selected-preview--red">{previewLabel}</span>
            : <span className="sm-none-hint">No reason selected</span>}
          <div className="sm-footer-actions">
            <button className="sm-btn-cancel" onClick={onClose}>Cancel</button>
            <button className="sm-btn-done sm-btn-done--red" disabled={!canConfirm} onClick={handleConfirm}>
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function Tag({ label, color }) {
  return (
    <span className="ap-tag" style={{ background: color + "18", color, borderColor: color + "40" }}>
      {label}
    </span>
  );
}

// ── Income flow ────────────────────────────────────────────────────────────
function IncomeSide({ onSubmit, onCancel }) {
  const { date, time } = getNow();
  const [step, setStep]                 = useState("amount");
  const [amount, setAmount]             = useState("");
  const [office, setOffice]             = useState("");
  const [person, setPerson]             = useState("");
  const [service, setService]           = useState("");
  const [charges, setCharges]           = useState("");
  const [showSvcModal, setShowSvcModal] = useState(false);
  const amountRef  = useRef();
  const personRef  = useRef();
  const chargesRef = useRef();

  useEffect(() => {
    if (step === "amount") amountRef.current?.focus();
    if (step === "person") personRef.current?.focus();
    if (step === "split")  chargesRef.current?.focus();
  }, [step]);

  useEffect(() => {
  if (step === "service" && office === "Amara Infinites") setShowSvcModal(true);
}, [step, office]);

  const parsed     = parseFloat(amount)  || 0;
  const parsedCh   = parseFloat(charges) || 0;
  const generating = Math.max(0, parsed - parsedCh);

  const handleSubmit = () => {
    onSubmit({ amount: parsed, generating, charges: parsedCh, office, person, service,
      purpose: `${service} – ${person}`, category: office });
  };

  return (
    <>
      <div className="ap-side ap-side--income">
        <div className="ap-meta">
          <span className="ap-meta__date">{date}</span>
          <span className="ap-meta__time">{time}</span>
        </div>
        <div className="ap-tags">
          {amount  && <Tag label={fmt(parsed)} color="#22c55e" />}
          {office  && <Tag label={office}      color="#3b82f6" />}
          {person  && <Tag label={person}      color="#a78bfa" />}
          {service && <Tag label={service}     color="#f59e0b" />}
        </div>

        {step === "amount" && (
          <div className="ap-step">
            <div className="ap-input-row">
              <span className="ap-currency">₹</span>
              <input ref={amountRef} className="ap-input ap-input--lg" type="number" placeholder="Amount"
                value={amount} onChange={(e) => setAmount(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && amount && setStep("office")} />
              <button className="ap-next ap-next--green" disabled={!amount} onClick={() => setStep("office")}>→</button>
            </div>
          </div>
        )}

        {step === "office" && (
          <div className="ap-step">
            <p className="ap-step-label">Office</p>
            <div className="ap-pills">
              {OFFICES.map((o) => (
                <button key={o} className={`ap-pill ap-pill--green ${office === o ? "ap-pill--on" : ""}`}
                  onClick={() => { setOffice(o); setStep("person"); }}>{o}</button>
              ))}
            </div>
          </div>
        )}

        {step === "person" && (
          <div className="ap-step">
            <p className="ap-step-label">Person</p>
            <div className="ap-input-row">
              <input ref={personRef} className="ap-input" type="text" placeholder="Name"
                value={person} onChange={(e) => setPerson(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && person.trim() && setStep("service")} />
              <button className="ap-next ap-next--green" disabled={!person.trim()} onClick={() => setStep("service")}>→</button>
            </div>
          </div>
        )}

        {step === "service" && !showSvcModal && (
          <div className="ap-step">
            <p className="ap-step-label">Service</p>
            {office === "Amara Infinites" ? (
              service ? (
                <div className="ap-input-row">
                  <div className="ap-service-selected">
                    <span className="ap-service-selected-name">{service}</span>
                    <button className="ap-service-change-btn" onClick={() => setShowSvcModal(true)}>Change</button>
                  </div>
                  <button className="ap-next ap-next--green" onClick={() => setStep("split")}>→</button>
                </div>
              ) : (
                <button className="ap-service-open-btn" onClick={() => setShowSvcModal(true)}>Browse Services ▼</button>
              )
            ) : (
              <div className="ap-input-row">
                <input
                  className="ap-input"
                  type="text"
                  placeholder="Enter service / description"
                  value={service}
                  onChange={(e) => setService(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && service.trim() && setStep("split")}
                  autoFocus
                />
                <button
                  className="ap-next ap-next--green"
                  disabled={!service.trim()}
                  onClick={() => setStep("split")}
                >→</button>
              </div>
            )}
          </div>
        )}

        {step === "split" && (
          <div className="ap-step">
            <p className="ap-step-label">Split</p>
            <div className="ap-split">
              <div className="ap-split-box">
                <span className="ap-split-lbl">Total</span>
                <span className="ap-split-val" style={{ color: "#22c55e" }}>{fmt(parsed)}</span>
              </div>
              <span className="ap-split-op">−</span>
              <div className="ap-split-box">
                <span className="ap-split-lbl">Charges</span>
                <div className="ap-input-row ap-input-row--tight">
                  <span className="ap-currency">₹</span>
                  <input ref={chargesRef} className="ap-input ap-input--sm" type="number" placeholder="0"
                    value={charges} onChange={(e) => setCharges(e.target.value)} />
                </div>
              </div>
              <span className="ap-split-op">=</span>
              <div className="ap-split-box">
                <span className="ap-split-lbl">Generating</span>
                <span className="ap-split-val" style={{ color: "#3b82f6" }}>{fmt(generating)}</span>
              </div>
            </div>
            <div className="ap-actions">
              <button className="ap-cancel" onClick={onCancel}>Cancel</button>
              <button className="ap-submit ap-submit--green" onClick={handleSubmit}>
                <i className="fa-solid fa-plus" /> Add Income
              </button>
            </div>
          </div>
        )}

        {step !== "split" && <button className="ap-cancel-link" onClick={onCancel}>✕ Cancel</button>}
      </div>

      {showSvcModal && (
        <ServiceModal
          onDone={(name) => { setService(name); setShowSvcModal(false); setStep("split"); }}
          onClose={() => { setShowSvcModal(false); if (!service) setStep("person"); }}
        />
      )}
    </>
  );
}

// ── Expense flow ───────────────────────────────────────────────────────────
function ExpenseSide({ onSubmit, onCancel }) {
  const { date, time } = getNow();
  const [step, setStep]                           = useState("amount");
  const [amount, setAmount]                       = useState("");
  const [source, setSource]                       = useState("");
  const [person, setPerson]                       = useState("");
  const [reason, setReason]                       = useState("");
  const [showReasonModal, setShowReasonModal]     = useState(false);
  const amountRef = useRef();
  const personRef = useRef();

  useEffect(() => {
    if (step === "amount") amountRef.current?.focus();
    if (step === "person") personRef.current?.focus();
  }, [step]);

  // Auto-open reason modal when step becomes "reason"
  useEffect(() => { if (step === "reason") setShowReasonModal(true); }, [step]);

  const parsed = parseFloat(amount) || 0;

  const handleSubmit = () => {
    onSubmit({ amount: parsed, category: source, purpose: reason, person,
      date: new Date().toISOString().split("T")[0], label: reason });
  };

  return (
    <>
      <div className="ap-side ap-side--expense">
        <div className="ap-meta">
          <span className="ap-meta__date">{date}</span>
          <span className="ap-meta__time">{time}</span>
        </div>
        <div className="ap-tags">
          {amount && <Tag label={fmt(parsed)} color="#ef4444" />}
          {source && <Tag label={source}      color="#f97316" />}
          {person && <Tag label={person}      color="#a78bfa" />}
          {reason && <Tag label={reason}      color="#ef4444" />}
        </div>

        {step === "amount" && (
          <div className="ap-step">
            <div className="ap-input-row">
              <span className="ap-currency">₹</span>
              <input ref={amountRef} className="ap-input ap-input--lg" type="number" placeholder="Amount"
                value={amount} onChange={(e) => setAmount(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && amount && setStep("source")} />
              <button className="ap-next ap-next--red" disabled={!amount} onClick={() => setStep("source")}>→</button>
            </div>
          </div>
        )}

        {step === "source" && (
          <div className="ap-step">
            <p className="ap-step-label">Source</p>
            <div className="ap-pills">
              {OFFICES.map((o) => (
                <button key={o} className={`ap-pill ap-pill--red ${source === o ? "ap-pill--on-red" : ""}`}
                  onClick={() => { setSource(o); setStep("person"); }}>{o}</button>
              ))}
            </div>
          </div>
        )}

        {step === "person" && (
          <div className="ap-step">
            <p className="ap-step-label">Person</p>
            <div className="ap-input-row">
              <input ref={personRef} className="ap-input" type="text" placeholder="Name"
                value={person} onChange={(e) => setPerson(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && person.trim() && setStep("reason")} />
              <button className="ap-next ap-next--red" disabled={!person.trim()} onClick={() => setStep("reason")}>→</button>
            </div>
          </div>
        )}

        {/* Reason step — selected reason chip + change + submit */}
        {step === "reason" && !showReasonModal && (
          <div className="ap-step">
            <p className="ap-step-label">Reason</p>
            {reason ? (
              <>
                <div className="ap-input-row">
                  <div className="ap-reason-selected">
                    <span className="ap-reason-selected-name">{reason}</span>
                    <button className="ap-reason-change-btn" onClick={() => setShowReasonModal(true)}>
                      Change
                    </button>
                  </div>
                </div>
                <div className="ap-actions">
                  <button className="ap-cancel" onClick={onCancel}>Cancel</button>
                  <button className="ap-submit ap-submit--red" onClick={handleSubmit}>
                    <i className="fa-solid fa-minus" /> Add Expense
                  </button>
                </div>
              </>
            ) : (
              <button className="ap-reason-open-btn" onClick={() => setShowReasonModal(true)}>
                Browse Reasons ▼
              </button>
            )}
          </div>
        )}

        {step !== "reason" && <button className="ap-cancel-link" onClick={onCancel}>✕ Cancel</button>}
      </div>

      {showReasonModal && (
        <ExpenseReasonModal
          onDone={(label) => { setReason(label); setShowReasonModal(false); }}
          onClose={() => { setShowReasonModal(false); if (!reason) setStep("person"); }}
        />
      )}
    </>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────
export default function ActionPanel() {
  const { dispatch } = useFinance();
  const [mode, setMode] = useState(null);

  const handleIncomeSubmit = (data) => {
    dispatch({
      type: "ADD_FUNDS",
      payload: { amount: data.amount, category: data.category, purpose: data.purpose,
        office: data.office, allocated: data.charges > 0 ? [{ label: "Charges", amount: data.charges }] : [] },
    });
    setMode(null);
  };

  const handleExpenseSubmit = (data) => {
    dispatch({ type: "ADD_SPEND", payload: data });
    setMode(null);
  };

  return (
    <div className="action-panel">
      <div className={`ap-half ap-half--income ${mode === "income" ? "ap-half--active" : ""}`}>
        <button className="ap-icon-btn" onClick={() => setMode(mode === "income" ? null : "income")}>
          <span className="ap-bar-icon ap-bar-icon--green">+</span>
        </button>
        {mode === "income" && <IncomeSide onSubmit={handleIncomeSubmit} onCancel={() => setMode(null)} />}
      </div>

      <div className="ap-divider" />

      <div className={`ap-half ap-half--expense ${mode === "expense" ? "ap-half--active" : ""}`}>
        <button className="ap-icon-btn" onClick={() => setMode(mode === "expense" ? null : "expense")}>
          <span className="ap-bar-icon ap-bar-icon--red">−</span>
        </button>
        {mode === "expense" && <ExpenseSide onSubmit={handleExpenseSubmit} onCancel={() => setMode(null)} />}
      </div>
    </div>
  );
}